import { createContext, useContext, useState, useEffect } from "react";
import { nanoid } from "nanoid";

const SessionContext = createContext();

const INTRO_MESSAGE = {
  role: "intro",
  content:
    "Hey there! I'm Anurag, and this little AI agent is me in digital form. Powered by LangChain, LLMs, RAG pipelines, and Pinecone embeddings, I can give you quick, context-aware answers. Wanna know about my projects, real-time AI systems, or scalable microservices? Just ask!",
  isRendered: false,
};

export const useCurrentChatSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error(
      "useCurrentChatSession must be used within a SessionProvider"
    );
  }
  return context;
};

export const SessionProvider = ({ children }) => {

  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([INTRO_MESSAGE]);
  const [isLoading, setIsLoading] = useState(true);
  const [isServerActive, setIsServerActive] = useState(false);

  useEffect(() => {
    // Get token from sessionStorage
    const existingToken = sessionStorage.getItem("token");

    if (existingToken) {
      // If token exists, use it
      setSessionId(existingToken);
      // Load saved messages for this session
      const savedMessages = sessionStorage.getItem(
        `chatMessages_${existingToken}`
      );
      if (savedMessages) {
        try {
          setMessages(JSON.parse(savedMessages));
        } catch (error) {
          console.error("Error parsing saved messages:", error);
          setMessages([INTRO_MESSAGE]);
        }
      }
    } else {
      // If no token exists, generate a new one
      const newToken = nanoid(6);
      sessionStorage.setItem("token", newToken);
      setSessionId(newToken);
      // Keep the intro message for new sessions
      setMessages([INTRO_MESSAGE]);
    }

    setIsLoading(false);
  }, []);

  // Function to save messages to sessionStorage
  const saveMessages = (newMessages) => {
    if (sessionId) {
      sessionStorage.setItem(
        `chatMessages_${sessionId}`,
        JSON.stringify(newMessages)
      );
    }
  };

  // Wrapper for setMessages that also saves to sessionStorage
  const setMessagesWithPersistence = (newMessages) => {
    setMessages(newMessages);
    saveMessages(newMessages);
  };

  const generateNewSession = () => {
    const newToken = nanoid(6);
    sessionStorage.setItem("token", newToken);
    setSessionId(newToken);
    setMessages([INTRO_MESSAGE]);
    return newToken;
  };

  const clearSession = () => {
    // Clear messages for current session
    if (sessionId) {
      sessionStorage.removeItem(`chatMessages_${sessionId}`);
    }
    sessionStorage.removeItem("token");
    setSessionId(null);
    setMessages([INTRO_MESSAGE]);
  };

  const value = {
    sessionId,
    messages,
    setMessages: setMessagesWithPersistence,
    isLoading,
    isServerActive,
    setIsServerActive,
    generateNewSession,
    clearSession,
  };

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};
